import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { errorMessage, useApi } from './client';
import { apiBaseUrl } from '../config';
import type {
  AccountSecurity,
  AdminUser,
  AdrTableEntry,
  AlertItem,
  AlertSummary,
  AnalysisDefinition,
  AnalysisKey,
  AnalysisResult,
  AnalysisSubscription,
  AnnualReport,
  AnnualReportSummary,
  AttachmentRecord,
  AuditFacet,
  AuditPage,
  Authorization,
  Bulletin,
  CalendarData,
  Cap,
  CapVoletAInfo,
  ChargementDetail,
  ChargementSignatures,
  ChargementSummary,
  ChecklistItem,
  ClientDetail,
  ClientTenant,
  CmdCode,
  Company,
  ComplianceReport,
  Conditionnement,
  ConsigneLanguages,
  Contract,
  DashboardData,
  DefaultRoute,
  Destination,
  DocumentRecord,
  DrCode,
  Expedition,
  ExpeditionCosts,
  ExpeditionSignatures,
  FixedCosts,
  GroupOverview,
  GroupRegistreResult,
  ImportBatch,
  ImportBatchSummary,
  IncomingTransfer,
  IndicatorsData,
  Installation,
  Invoice,
  InvoiceCostLine,
  Me,
  Origin,
  OtherDocument,
  PerformanceData,
  PickupRequest,
  PivotResult,
  PreparationItem,
  Prestataire,
  RefImpacts,
  RegistreClosure,
  RegistreResult,
  ReturnsResult,
  RouteRow,
  SavedAnalysis,
  Site,
  SiteActivity,
  SiteAlertSettings,
  SiteProgress,
  SiteSummary,
  StockBalance,
  StockMovement,
  TenantExport,
  TenantUsage,
  Waste,
  BulletinPublication,
  RefVersion,
} from './types';

export const keys = {
  me: ['me'] as const,
  summary: (siteId: string) => ['summary', siteId] as const,
  site: (siteId: string) => ['site', siteId] as const,
  sites: ['sites'] as const,
  companies: ['companies'] as const,
  origins: (siteId: string) => ['origins', siteId] as const,
  wastes: (siteId: string) => ['wastes', siteId] as const,
  waste: (id: string) => ['waste', id] as const,
  route: (wasteId: string) => ['route', wasteId] as const,
  wasteRoutes: (wasteId: string) => ['waste-routes', wasteId] as const,
  conditionnements: (siteId: string) => ['conditionnements', siteId] as const,
  prestataires: (siteId: string) => ['prestataires', siteId] as const,
  prestataire: (id: string) => ['prestataire', id] as const,
  installations: (id: string) => ['installations', id] as const,
  authorizations: (id: string) => ['authorizations', id] as const,
  otherDocuments: (holder: string) => ['other-documents', holder] as const,
  destinations: (siteId: string) => ['destinations', siteId] as const,
  caps: (siteId: string) => ['caps', siteId] as const,
  cmd: (params: string) => ['cmd', params] as const,
  cmdCode: (code: string) => ['cmdCode', code] as const,
  adr: (un: string) => ['adr', un] as const,
  dr: ['dr'] as const,
  consignes: ['consignes-adr'] as const,
  expeditions: (siteId: string) => ['expeditions', siteId] as const,
  expedition: (id: string) => ['expedition', id] as const,
  chargements: (siteId: string) => ['chargements', siteId] as const,
  chargement: (id: string) => ['chargement', id] as const,
  pickupRequests: (siteId: string) => ['pickupRequests', siteId] as const,
  stock: (siteId: string) => ['stock', siteId] as const,
  stockMovements: (siteId: string) => ['stockMovements', siteId] as const,
  registre: (siteId: string) => ['registre', siteId] as const,
  documents: (ownerType: string, ownerId: string) => ['documents', ownerType, ownerId] as const,
  attachments: (ownerType: string, ownerId: string) => ['attachments', ownerType, ownerId] as const,
  checklist: (chargementId: string) => ['checklist', chargementId] as const,
  annualReports: (siteId: string) => ['annualReports', siteId] as const,
  annualReport: (id: string) => ['annualReport', id] as const,
  users: ['users'] as const,
  tenants: ['tenants'] as const,
  contracts: ['contracts'] as const,
  invoices: ['invoices'] as const,
  transfers: (siteId: string) => ['transfers', siteId] as const,
  compliance: (siteId: string) => ['compliance', siteId] as const,
  expeditionCosts: (id: string) => ['expeditionCosts', id] as const,
  audit: ['audit'] as const,
  alerts: ['alerts'] as const,
  alertSummary: ['alertSummary'] as const,
  bulletins: ['bulletins'] as const,
  alertSettings: (siteId: string) => ['alertSettings', siteId] as const,
  returns: (siteId: string) => ['returns', siteId] as const,
  dashboard: (siteId: string) => ['dashboard', siteId] as const,
  closures: (siteId: string) => ['closures', siteId] as const,
  calendar: (siteId: string) => ['calendar', siteId] as const,
  performance: (siteId: string) => ['performance', siteId] as const,
  indicators: (siteId: string) => ['indicators', siteId] as const,
  usage: ['usage'] as const,
  refImpacts: ['ref-impacts'] as const,
  fixedCosts: (siteId: string) => ['fixed-costs', siteId] as const,
  group: ['group'] as const,
  groupRegistre: ['group-registre'] as const,
  activity: (siteId: string) => ['activity', siteId] as const,
  signatures: (expeditionId: string) => ['signatures', expeditionId] as const,
  analyses: ['analyses'] as const,
  chargementSignatures: (chargementId: string) => ['chargement-signatures', chargementId] as const,
  siteTargets: (siteId: string) => ['site-targets', siteId] as const,
  exports: ['exports'] as const,
  imports: (siteId: string) => ['imports', siteId] as const,
  routes: (siteId: string) => ['routes', siteId] as const,
  capVoletA: (capId: string) => ['capVoletA', capId] as const,
  importBatch: (id: string) => ['import', id] as const,
};

/** Every list an import can change, refreshed after a commit or a reversal. */
export const importedKeys: QueryKey[] = [
  ['imports'],
  ['import'],
  ['summary'],
  ['origins'],
  ['wastes'],
  ['waste'],
  ['route'],
  ['conditionnements'],
  ['prestataires'],
  ['prestataire'],
  ['installations'],
  ['authorizations'],
  ['destinations'],
  ['caps'],
  ['users'],
  ['alerts'],
  ['alertSummary'],
  ['dashboard'],
  ['stock'],
  ['stockMovements'],
  ['registre'],
  ['expeditions'],
  ['expedition'],
  ['chargements'],
  ['chargement'],
  ['invoices'],
  ['returns'],
];

/** Every query a Collecte action can change on a site. */
export const collecteKeys = (siteId: string) => [
  keys.expeditions(siteId),
  keys.chargements(siteId),
  keys.pickupRequests(siteId),
  keys.stock(siteId),
  keys.stockMovements(siteId),
  keys.registre(siteId),
  keys.summary(siteId),
  ['expedition'],
  ['chargement'],
  ['documents'],
  ['attachments'],
  ['checklist'],
  ['alerts'],
  ['alertSummary'],
  ['returns'],
  ['dashboard'],
  // every transition recomputes the costs, which the invoices reconcile against
  ['expeditionCosts'],
  keys.invoices,
];

export const useMe = () => {
  const api = useApi();
  return useQuery({ queryKey: keys.me, queryFn: () => api<Me>('/me'), staleTime: 60_000 });
};

/** Two-step verification and the second factors of the signed-in user (ADR-0006). */
export const useAccountSecurity = (enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.me, 'security'],
    queryFn: () => api<AccountSecurity>('/me/security'),
    enabled,
  });
};

export const useSiteSummary = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.summary(siteId ?? ''),
    queryFn: () => api<SiteSummary>(`/sites/${siteId}/summary`),
    enabled: Boolean(siteId),
  });
};

export const useSite = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.site(siteId ?? ''),
    queryFn: () => api<Site>(`/sites/${siteId}`),
    enabled: Boolean(siteId),
  });
};

export const useSites = () => {
  const api = useApi();
  return useQuery({ queryKey: keys.sites, queryFn: () => api<Site[]>('/sites') });
};

export const useCompanies = () => {
  const api = useApi();
  return useQuery({ queryKey: keys.companies, queryFn: () => api<Company[]>('/companies') });
};

export const useOrigins = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.origins(siteId ?? ''),
    queryFn: () => api<Origin[]>(`/sites/${siteId}/origins`),
    enabled: Boolean(siteId),
  });
};

export const useWastes = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.wastes(siteId ?? ''),
    queryFn: () => api<Waste[]>(`/sites/${siteId}/wastes?includeInactive=true`),
    enabled: Boolean(siteId),
  });
};

export const useWaste = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.waste(id ?? ''),
    queryFn: () => api<Waste>(`/wastes/${id}`),
    enabled: Boolean(id),
  });
};

export const useDefaultRoute = (wasteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.route(wasteId ?? ''),
    queryFn: () => api<DefaultRoute | null>(`/wastes/${wasteId}/default-route`),
    enabled: Boolean(wasteId),
  });
};

/** Every filière of a waste, the default one first (FR-PAR-44). */
export const useWasteRoutes = (wasteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.wasteRoutes(wasteId ?? ''),
    queryFn: () => api<DefaultRoute[]>(`/wastes/${wasteId}/routes`),
    enabled: Boolean(wasteId),
  });
};

export const useConditionnements = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.conditionnements(siteId ?? ''),
    queryFn: () => api<Conditionnement[]>(`/sites/${siteId}/conditionnements?includeInactive=true`),
    enabled: Boolean(siteId),
  });
};

export const usePrestataires = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.prestataires(siteId ?? ''),
    queryFn: () =>
      api<Prestataire[]>(`/prestataires?includeInactive=true${siteId ? `&siteId=${siteId}` : ''}`),
  });
};

export const usePrestataire = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.prestataire(id ?? ''),
    queryFn: () => api<Prestataire>(`/prestataires/${id}`),
    enabled: Boolean(id),
  });
};

export const useInstallations = (prestataireId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.installations(prestataireId ?? ''),
    queryFn: () => api<Installation[]>(`/prestataires/${prestataireId}/installations`),
    enabled: Boolean(prestataireId),
  });
};

export const useAuthorizations = (prestataireId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.authorizations(prestataireId ?? ''),
    queryFn: () => api<Authorization[]>(`/prestataires/${prestataireId}/authorizations`),
    enabled: Boolean(prestataireId),
  });
};

/** The other documents of a site or of a prestataire (FR-PAR-34). */
export const useOtherDocuments = (holder: { siteId?: string; prestataireId?: string }) => {
  const api = useApi();
  const query = holder.siteId ? `siteId=${holder.siteId}` : `prestataireId=${holder.prestataireId}`;
  return useQuery({
    queryKey: keys.otherDocuments(holder.siteId ?? holder.prestataireId ?? ''),
    queryFn: () => api<OtherDocument[]>(`/other-documents?${query}`),
    enabled: Boolean(holder.siteId ?? holder.prestataireId),
  });
};

export const useDestinations = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.destinations(siteId ?? ''),
    queryFn: () => api<Destination[]>(`/sites/${siteId}/destinations`),
    enabled: Boolean(siteId),
  });
};

export const useCaps = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.caps(siteId ?? ''),
    queryFn: () => api<Cap[]>(`/sites/${siteId}/caps`),
    enabled: Boolean(siteId),
  });
};

export const useCmdSearch = (params: URLSearchParams, enabled = true) => {
  const api = useApi();
  const qs = params.toString();
  return useQuery({
    queryKey: keys.cmd(qs),
    queryFn: () => api<CmdCode[]>(`/ref/cmd-codes?${qs}`),
    enabled,
    staleTime: 10 * 60_000,
    placeholderData: (prev) => prev,
  });
};

export const useCmdCode = (code?: string | null) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.cmdCode(code ?? ''),
    queryFn: () => api<CmdCode>(`/ref/cmd-codes/${encodeURIComponent(code!.replace(/ /g, ''))}`),
    enabled: Boolean(code && /^\d{2}( ?\d{2}){2}$/.test(code)),
    staleTime: 60 * 60_000,
  });
};

export const useAdr = (un?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.adr(un ?? ''),
    queryFn: () => api<AdrTableEntry[]>(`/ref/adr?un=${un}`),
    enabled: Boolean(un && /^\d{4}$/.test(un)),
    staleTime: 60 * 60_000,
  });
};

export const useDrCodes = () => {
  const api = useApi();
  return useQuery({
    queryKey: keys.dr,
    queryFn: () => api<DrCode[]>('/ref/dr-codes'),
    staleTime: 60 * 60_000,
  });
};

/** FR-FIA-17: the languages whose official ADR written instructions are loaded. */
export const useConsigneLanguages = () => {
  const api = useApi();
  return useQuery({
    queryKey: keys.consignes,
    queryFn: () => api<ConsigneLanguages>('/ref/consignes-adr'),
    staleTime: 60_000,
  });
};

/** D/R operations with labels in the user's language, for selects and tooltips. */
export function useDrOptions() {
  const { i18n } = useTranslation();
  const dr = useDrCodes();
  const lang = i18n.language.slice(0, 2);
  const labelOf = (d: DrCode) =>
    (lang === 'en' ? d.labelEn : lang === 'ar' ? d.labelAr : null) ?? d.labelFr;
  const label = (code: string) => {
    const d = dr.data?.find((x) => x.code === code);
    return d ? `${code} — ${labelOf(d)}` : code;
  };
  const options = (codes?: string[]) =>
    (codes ?? (dr.data ?? []).map((d) => d.code)).map((c) => ({ value: c, label: label(c) }));
  return { label, options, loading: dr.isLoading };
}

/**
 * A mutation that shows a success toast, invalidates the given queries and reports errors
 * (the caller may also map field violations onto its form).
 */
export function useApiMutation<TVars, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  /** success: the message shown once done, or false when the caller tells the outcome itself */
  options: { invalidate?: QueryKey[]; success?: string | false; silentError?: boolean } = {},
) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(
        (options.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k })),
      );
      if (options.success !== false) message.success(options.success ?? t('common.saved'));
    },
    onError: (error) => {
      if (!options.silentError) message.error(errorMessage(error));
    },
  });
}

export const useExpeditions = (
  siteId: string | undefined,
  params: Record<string, string | undefined> = {},
) => {
  const api = useApi();
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v) as [string, string][],
  ).toString();
  return useQuery({
    queryKey: [...keys.expeditions(siteId ?? ''), query],
    queryFn: () => api<Expedition[]>(`/sites/${siteId}/expeditions${query ? `?${query}` : ''}`),
    enabled: Boolean(siteId),
  });
};

export const useExpedition = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.expedition(id ?? ''),
    queryFn: () => api<Expedition>(`/expeditions/${id}`),
    enabled: Boolean(id),
  });
};

export const useChargements = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.chargements(siteId ?? ''),
    queryFn: () => api<ChargementSummary[]>(`/sites/${siteId}/chargements`),
    enabled: Boolean(siteId),
  });
};

export const useChargement = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.chargement(id ?? ''),
    queryFn: () => api<ChargementDetail>(`/chargements/${id}`),
    enabled: Boolean(id),
  });
};

export const usePickupRequests = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.pickupRequests(siteId ?? ''),
    queryFn: () => api<PickupRequest[]>(`/sites/${siteId}/pickup-requests`),
    enabled: Boolean(siteId),
  });
};

export const useStock = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.stock(siteId ?? ''),
    queryFn: () => api<StockBalance[]>(`/sites/${siteId}/stock`),
    enabled: Boolean(siteId),
  });
};

export const useStockMovements = (siteId?: string, wasteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.stockMovements(siteId ?? ''), wasteId ?? ''],
    queryFn: () =>
      api<StockMovement[]>(
        `/sites/${siteId}/stock-movements${wasteId ? `?wasteId=${wasteId}` : ''}`,
      ),
    enabled: Boolean(siteId),
  });
};

export const useRegistre = (
  siteId: string | undefined,
  filters: Record<string, string | undefined>,
) => {
  const api = useApi();
  const query = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v) as [string, string][],
  ).toString();
  return useQuery({
    queryKey: [...keys.registre(siteId ?? ''), query],
    queryFn: () => api<RegistreResult>(`/sites/${siteId}/registre${query ? `?${query}` : ''}`),
    enabled: Boolean(siteId),
  });
};

export const useDocuments = (
  ownerType: 'expedition' | 'chargement' | 'waste',
  ownerId?: string,
) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.documents(ownerType, ownerId ?? ''),
    queryFn: () => api<DocumentRecord[]>(`/${ownerType}s/${ownerId}/documents`),
    enabled: Boolean(ownerId),
  });
};

const OWNER_PATHS = {
  expedition: 'expeditions',
  chargement: 'chargements',
  annual_report: 'annual-reports',
  waste: 'wastes',
  cap: 'caps',
  pickup_request: 'pickup-requests',
  prestataire_authorization: 'authorizations',
  other_document: 'other-documents',
  invoice: 'invoices',
} as const;
export type AttachmentOwner = keyof typeof OWNER_PATHS;
export const attachmentsPath = (ownerType: AttachmentOwner, ownerId: string) =>
  `/${OWNER_PATHS[ownerType]}/${ownerId}/attachments`;

export const useAttachments = (ownerType: AttachmentOwner, ownerId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.attachments(ownerType, ownerId ?? ''),
    queryFn: () => api<AttachmentRecord[]>(attachmentsPath(ownerType, ownerId!)),
    enabled: Boolean(ownerId),
  });
};

export const useChecklist = (chargementId?: string, date?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.checklist(chargementId ?? ''), date ?? ''],
    queryFn: () =>
      api<ChecklistItem[]>(`/chargements/${chargementId}/checklist${date ? `?date=${date}` : ''}`),
    enabled: Boolean(chargementId),
  });
};

export const useAnnualReports = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.annualReports(siteId ?? ''),
    queryFn: () => api<AnnualReportSummary[]>(`/sites/${siteId}/annual-reports`),
    enabled: Boolean(siteId),
  });
};

export const useAnnualReport = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.annualReport(id ?? ''),
    queryFn: () => api<AnnualReport>(`/annual-reports/${id}`),
    enabled: Boolean(id),
  });
};

export const usePreparation = (siteId?: string, year?: number) => {
  const api = useApi();
  return useQuery({
    queryKey: ['preparation', siteId ?? '', year ?? 0],
    queryFn: () =>
      api<PreparationItem[]>(`/sites/${siteId}/annual-reports/preparation?year=${year}`),
    enabled: Boolean(siteId && year),
  });
};

// ---- administration ----------------------------------------------------------------------------

/** Contracts with the prestataires (FR-SUI-11), optionally of one prestataire. */
export const useContracts = (
  params: { prestataireId?: string; includeInactive?: boolean } = {},
) => {
  const api = useApi();
  const query = new URLSearchParams(
    Object.entries({
      prestataireId: params.prestataireId,
      includeInactive: params.includeInactive ? 'true' : undefined,
    }).filter(([, v]) => v) as [string, string][],
  ).toString();
  return useQuery({
    queryKey: [...keys.contracts, query],
    queryFn: () => api<Contract[]>(`/contracts${query ? `?${query}` : ''}`),
  });
};

/** The cost lines of an expédition, for the roles that see costs (FR-SUI-10). */
/** The transfers sent to a site from other sites of the tenant (FR-COL-27). */
export const useIncomingTransfers = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.transfers(siteId ?? ''),
    queryFn: () => api<IncomingTransfer[]>(`/sites/${siteId}/transfers`),
    enabled: Boolean(siteId),
  });
};

/** Prestataire invoices with their reconciliation (FR-SUI-14). */
export const useInvoices = (
  params: { prestataireId?: string; companyId?: string; status?: string } = {},
  enabled = true,
) => {
  const api = useApi();
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v) as [string, string][],
  ).toString();
  return useQuery({
    queryKey: [...keys.invoices, query],
    queryFn: () => api<Invoice[]>(`/invoices${query ? `?${query}` : ''}`),
    enabled,
  });
};

export const useInvoice = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.invoices, 'one', id ?? ''],
    queryFn: () => api<Invoice>(`/invoices/${id}`),
    enabled: Boolean(id),
  });
};

/** The cost lines of a prestataire a new invoice may match, over a period. */
export const useInvoiceCandidates = (
  params: { prestataireId?: string; companyId?: string; from?: string; to?: string },
  enabled = true,
) => {
  const api = useApi();
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v) as [string, string][],
  ).toString();
  return useQuery({
    queryKey: [...keys.invoices, 'candidates', query],
    queryFn: () => api<InvoiceCostLine[]>(`/invoices/candidates?${query}`),
    enabled:
      enabled && Boolean(params.prestataireId && params.companyId && params.from && params.to),
  });
};

export const useExpeditionCosts = (id?: string, enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.expeditionCosts(id ?? ''),
    queryFn: () => api<ExpeditionCosts>(`/expeditions/${id}/costs`),
    enabled: Boolean(id) && enabled,
  });
};

/** The site compliance report at a date, with the exemptions of a period (FR-FIA-23). */
export const useComplianceReport = (
  siteId?: string,
  params: { date?: string; from?: string; to?: string } = {},
) => {
  const api = useApi();
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v) as [string, string][],
  ).toString();
  return useQuery({
    queryKey: [...keys.compliance(siteId ?? ''), query],
    queryFn: () =>
      api<ComplianceReport>(`/sites/${siteId}/compliance-report${query ? `?${query}` : ''}`),
    enabled: Boolean(siteId),
  });
};

/** The vendor's clients (platform operators). */
export const useTenants = () => {
  const api = useApi();
  return useQuery({ queryKey: keys.tenants, queryFn: () => api<ClientTenant[]>('/tenants') });
};

/** One client of the vendor, its set-up, activity and signals (platform operators). */
export const useClient = (id: string | undefined) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.tenants, id],
    queryFn: () => api<ClientDetail>(`/tenants/${id}`),
    enabled: Boolean(id),
  });
};

/** The bulletins the vendor published, withdrawn ones too (platform operators). */
export const useBulletinPublications = () => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.bulletins, 'publications'],
    queryFn: () =>
      api<{ clients: number; bulletins: BulletinPublication[] }>('/bulletins/publications'),
  });
};

/** The published versions of the reference data (CMD, D/R, ADR...). */
export const useRefVersions = (enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: ['ref-versions'],
    queryFn: () => api<RefVersion[]>('/ref/versions'),
    enabled,
    staleTime: 60 * 60 * 1000,
  });
};

export const useUsers = (enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.users,
    queryFn: () => api<AdminUser[]>('/users'),
    enabled,
  });
};

/** The audit log, most recent first, loaded page after page (FR-TRV-15). */
export const useAuditLog = (filters: Record<string, string | undefined>) => {
  const api = useApi();
  const base = Object.entries(filters).filter(([, v]) => v) as [string, string][];
  return useInfiniteQuery({
    queryKey: [...keys.audit, new URLSearchParams(base).toString()],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams([...base, ['limit', '100']]);
      if (pageParam) q.set('before', pageParam);
      return api<AuditPage>(`/audit?${q}`);
    },
    getNextPageParam: (last) => last.next ?? undefined,
  });
};

export const useAuditFacets = () => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.audit, 'facets'],
    queryFn: () => api<AuditFacet[]>('/audit/facets'),
    staleTime: 60_000,
  });
};

// ---- alerts and notifications ------------------------------------------------------------------

/** The badge of the notification centre, refreshed every minute; for one site on its dashboard. */
export const useAlertSummary = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.alertSummary, siteId ?? 'all'],
    queryFn: () => api<AlertSummary>(`/alerts/summary${siteId ? `?siteId=${siteId}` : ''}`),
    refetchInterval: 60_000,
  });
};

export const useAlerts = (view: 'open' | 'done', enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.alerts, view],
    queryFn: () => api<AlertItem[]>(`/alerts?view=${view}`),
    enabled,
  });
};

export const useBulletins = (enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.bulletins,
    queryFn: () => api<Bulletin[]>('/bulletins'),
    enabled,
  });
};

export const useAlertSettings = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.alertSettings(siteId ?? ''),
    queryFn: () => api<SiteAlertSettings>(`/sites/${siteId}/alert-settings`),
    enabled: Boolean(siteId),
  });
};

/** Signed BSD copies awaited, by prestataire (FR-SUI-03). */
export const useReturns = (siteId: string | undefined, scope: 'overdue' | 'pending') => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.returns(siteId ?? ''), scope],
    queryFn: () => api<ReturnsResult>(`/sites/${siteId}/returns?scope=${scope}`),
    enabled: Boolean(siteId),
  });
};

/** Figures of the year, monthly trend and task list of a site (FR-PIL-01, FR-PIL-02). */
export const useDashboard = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.dashboard(siteId ?? ''),
    queryFn: () => api<DashboardData>(`/sites/${siteId}/dashboard`),
    enabled: Boolean(siteId),
  });
};

// ---- Excel import ------------------------------------------------------------------------------

/** Imports of the site and of the organisation (FR-PAR-46). */
export const useImports = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.imports(siteId ?? ''),
    queryFn: () => api<ImportBatchSummary[]>(`/imports${siteId ? `?siteId=${siteId}` : ''}`),
  });
};

/** An import with its line-by-line report. */
export const useImportBatch = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.importBatch(id ?? ''),
    queryFn: () => api<ImportBatch>(`/imports/${id}`),
    enabled: Boolean(id),
  });
};

/** The filières par défaut of a site, one line per waste (FR-PAR-45). */
export const useRoutes = (siteId?: string, includeInactive = false) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.routes(siteId ?? ''), includeInactive],
    queryFn: () =>
      api<RouteRow[]>(`/sites/${siteId}/default-routes?includeInactive=${includeInactive}`),
    enabled: Boolean(siteId),
  });
};

/** The last sending of a CAP's volet A and the suggested recipients (FR-PAR-37). */
export const useCapVoletA = (capId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.capVoletA(capId ?? ''),
    queryFn: () => api<CapVoletAInfo>(`/caps/${capId}/volet-a`),
    enabled: Boolean(capId),
  });
};

/** The closures of the registre of a site, latest first (FR-CON-06). */
export const useRegistreClosures = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.closures(siteId ?? ''),
    queryFn: () => api<RegistreClosure[]>(`/sites/${siteId}/registre/closures`),
    enabled: Boolean(siteId),
  });
};

/** The regulatory calendar of a site (FR-TRV-10). */
export const useCalendar = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.calendar(siteId ?? ''),
    queryFn: () => api<CalendarData>(`/sites/${siteId}/calendar`),
    enabled: Boolean(siteId),
  });
};

/** The prestataire performance of a site over a period (FR-SUI-18). */
export const usePrestatairePerformance = (siteId?: string, from?: string, to?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.performance(siteId ?? ''), from ?? '', to ?? ''],
    queryFn: () =>
      api<PerformanceData>(`/sites/${siteId}/prestataires/performance?from=${from}&to=${to}`),
    enabled: Boolean(siteId && from && to),
  });
};

/** The environmental indicators of a site over a period (FR-CON-21, FR-PIL-13). */
export const useIndicators = (siteId?: string, from?: string, to?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.indicators(siteId ?? ''), from ?? '', to ?? ''],
    queryFn: () => api<IndicatorsData>(`/sites/${siteId}/indicators?from=${from}&to=${to}`),
    enabled: Boolean(siteId && from && to),
  });
};

/** Usage of a tenant over a period (FR-TRV-17); `path` is `/usage` or `/tenants/:id/usage`. */
export const useUsage = (path: string, from: string, to: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.usage, path, from, to],
    queryFn: () => api<TenantUsage>(`${path}?from=${from}&to=${to}`),
  });
};

/** The full data exports of the tenant (FR-TRV-18). */
export const useTenantExports = (enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.exports,
    queryFn: () => api<TenantExport[]>('/exports'),
    enabled,
  });
};

/** Wastes and filières impacted by the reference-data versions in force (FR-PAR-10, FR-TRV-07). */
export const useRefImpacts = (enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.refImpacts,
    queryFn: () => api<RefImpacts>('/ref/impacts'),
    enabled,
  });
};

/** The fixed costs of a site over a range of months (FR-SUI-12). */
export const useFixedCosts = (
  siteId: string | undefined,
  from: string,
  to: string,
  enabled = true,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.fixedCosts(siteId ?? ''), from, to],
    queryFn: () => api<FixedCosts>(`/sites/${siteId}/fixed-costs?from=${from}&to=${to}`),
    enabled: Boolean(siteId) && enabled,
  });
};

/** The group view of a year (FR-PIL-09, FR-PIL-12, FR-CON-13). */
export const useGroupOverview = (year: number, enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.group, year],
    queryFn: () => api<GroupOverview>(`/group/overview?year=${year}`),
    enabled,
  });
};

/** The registre of several sites with the filters of the site registre (FR-CON-19). */
export const useGroupRegistre = (filters: Record<string, string | undefined>) => {
  const api = useApi();
  const query = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v) as [string, string][],
  ).toString();
  return useQuery({
    queryKey: [...keys.groupRegistre, query],
    queryFn: () => api<GroupRegistreResult>(`/group/registre${query ? `?${query}` : ''}`),
  });
};

/** The monthly activity of a site for a year (FR-PIL-10). */
export const useSiteActivity = (siteId: string | undefined, year: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.activity(siteId ?? ''), year],
    queryFn: () => api<SiteActivity>(`/sites/${siteId}/activity/${year}`),
    enabled: Boolean(siteId),
  });
};

/** The intensity ratios of a site and its progress against the group targets (FR-PIL-11). */
export const useSiteTargets = (siteId: string | undefined, year?: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.siteTargets(siteId ?? ''), year ?? 'current'],
    queryFn: () => api<SiteProgress>(`/sites/${siteId}/targets${year ? `?year=${year}` : ''}`),
    enabled: Boolean(siteId),
  });
};

/** The in-app signatures of a bordereau and the steps the user may sign (FR-FIA-19). */
export const useExpeditionSignatures = (expeditionId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.signatures(expeditionId ?? ''),
    queryFn: () => api<ExpeditionSignatures>(`/expeditions/${expeditionId}/signatures`),
    enabled: Boolean(expeditionId),
  });
};

/** The signatures of the bordereaux of a chargement, step by step (FR-FIA-19). */
export const useChargementSignatures = (chargementId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: keys.chargementSignatures(chargementId ?? ''),
    queryFn: () => api<ChargementSignatures>(`/chargements/${chargementId}/signatures`),
    enabled: Boolean(chargementId),
  });
};

/** The catalogue of ready analyses (FR-PIL-04), with what the role may run on the site. */
export const useAnalysesCatalogue = (siteId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.analyses, 'catalogue', siteId ?? 'group'],
    queryFn: () => api<AnalysisDefinition[]>(`/analyses${siteId ? `?siteId=${siteId}` : ''}`),
  });
};

/** One analysis of a site, or of the group, with its filters as query parameters. */
export const useAnalysis = (
  scope: { siteId: string } | { group: true },
  key: AnalysisKey | undefined,
  params: Record<string, string | undefined>,
) => {
  const api = useApi();
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v) as [string, string][],
  ).toString();
  const path =
    'group' in scope ? `/group/analyses/${key}` : `/sites/${scope.siteId}/analyses/${key}`;
  return useQuery({
    queryKey: [...keys.analyses, path, query],
    queryFn: () => api<AnalysisResult>(`${path}${query ? `?${query}` : ''}`),
    enabled: Boolean(key) && ('group' in scope || Boolean(scope.siteId)),
    placeholderData: (prev) => prev,
  });
};

/** The pivot builder of a site, or of the group (FR-PIL-07), with its query as URL parameters. */
export const usePivot = (
  scope: { siteId: string } | { group: true },
  params: Record<string, string | undefined>,
  enabled = true,
) => {
  const api = useApi();
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v) as [string, string][],
  ).toString();
  const path = 'group' in scope ? '/group/pivot' : `/sites/${scope.siteId}/pivot`;
  return useQuery({
    queryKey: [...keys.analyses, path, query],
    queryFn: () => api<PivotResult>(`${path}?${query}`),
    enabled: enabled && ('group' in scope || Boolean(scope.siteId)),
    placeholderData: (prev) => prev,
  });
};

/** The analyses e-mailed every month for a site, or for the group (FR-PIL-05). */
export const useAnalysisSubscriptions = (siteId: string | null | undefined, enabled = true) => {
  const api = useApi();
  const path = siteId ? `/sites/${siteId}/analyses-subscriptions` : '/group/analyses-subscriptions';
  return useQuery({
    queryKey: [...keys.analyses, 'subscriptions', siteId ?? 'group'],
    queryFn: () => api<AnalysisSubscription[]>(path),
    enabled: enabled && siteId !== undefined,
  });
};

/** The saved analyses of a site, or of the group: mine and the shared ones (FR-PIL-08). */
export const useSavedAnalyses = (siteId: string | null | undefined, enabled = true) => {
  const api = useApi();
  const path = siteId ? `/sites/${siteId}/saved-analyses` : '/group/saved-analyses';
  return useQuery({
    queryKey: [...keys.analyses, 'saved', siteId ?? 'group'],
    queryFn: () => api<SavedAnalysis[]>(path),
    enabled: enabled && siteId !== undefined,
  });
};

/** How to reach the vendor (support and contact addresses), public, for the help menu. */
export const usePublicContact = () =>
  useQuery({
    queryKey: ['public-contact'],
    queryFn: async () => {
      const r = await fetch(`${apiBaseUrl}/public/contact`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return (await r.json()) as {
        support: string | null;
        contact: string | null;
        /** the company named in the privacy notice; older servers send none */
        operator?: { name: string | null; address: string | null; cndpDeclaration: string | null };
      };
    },
    staleTime: Infinity,
  });
